// In-memory cart store for the Standard Ordering flow.
// Deliberately NOT persisted — a training sandbox starts clean on every reload.

import { create } from 'zustand'
import { MOCK_QPO_ITEMS } from '@/screens/ordering/mock-data'
import type {
  CartLine,
  QpoItem,
  QpoSession,
  Sku,
  TempCheckCategory,
} from '@/screens/ordering/types'
import { initialClassTempMap } from '@/screens/ordering/utils/qpo-utils'

let lineSeq = 0
function nextLineId(): string {
  lineSeq += 1
  return `line-${lineSeq}`
}

interface OrderingState {
  lines: CartLine[]
  /** Add a SKU to the cart. Merges qty when the same SKU + delivery date already exists. */
  addLine: (sku: Sku, qty: number, deliveryDate: string) => void
  removeLine: (lineId: string) => void
  updateQty: (lineId: string, qty: number) => void
  updateDeliveryDate: (lineId: string, deliveryDate: string) => void
  toggleSelected: (lineId: string) => void
  clear: () => void
  /** Full reset of all ordering state — lines, QPO, and held QPOs. Called on lesson start. */
  resetAll: () => void

  // ── QPO slice ────────────────────────────────────────────────────────────
  qpo: QpoSession
  setQpoSupplierInfo: (info: Omit<QpoSession, 'items'>) => void
  addInitialQpo: (qpoItem: QpoItem) => void
  resetQpoItems: () => void
  /** Replace the QPO lines wholesale — e.g. a supplier QR scan loading a delivery manifest. */
  loadQpoItems: (items: QpoItem[]) => void
  updateQpoItem: (skuId: string, patch: Partial<Pick<QpoItem, 'grQty' | 'temp'>>) => void
  deleteQpoItem: (skuId: string) => void
  resetQpo: () => void
  heldQpos: QpoSession[]
  holdQpo: () => void
  resumeQpo: (invoiceNo: string) => void
  submitQpo: () => void
  updateClassTemp: (category: TempCheckCategory, temp: string) => void
  confirmClassTemp: (category: TempCheckCategory) => void
  confirmClassTempAsFeedback: (category: TempCheckCategory) => void
  rejectClassTemp: (category: TempCheckCategory) => void
  resetClassTemp: (category: TempCheckCategory) => void
}

const INITIAL_QPO: QpoSession = {
  supplier: null,
  driverName: '',
  employeeCode: '',
  vehicleNo: '',
  invoiceNo: '',
  deliveryDate: '2026-09-12',
  // Starts empty: each SKU is added individually (scanned or searched) so the
  // QPO Details page reflects exactly what the learner has added, never a
  // pre-populated list.
  items: [],
  status: 'active',
  classTempMap: initialClassTempMap(),
}

export const useOrderingStore = create<OrderingState>((set) => ({
  lines: [],

  addLine: (sku, qty, deliveryDate) =>
    set((state) => {
      const existing = state.lines.find(
        (l) => l.sku.id === sku.id && l.deliveryDate === deliveryDate,
      )
      if (existing) {
        return {
          lines: state.lines.map((l) =>
            l.lineId === existing.lineId ? { ...l, qty: l.qty + qty } : l,
          ),
        }
      }
      const line: CartLine = {
        lineId: nextLineId(),
        sku,
        qty,
        deliveryDate,
        selected: true,
      }
      return { lines: [...state.lines, line] }
    }),

  removeLine: (lineId) =>
    set((state) => ({ lines: state.lines.filter((l) => l.lineId !== lineId) })),

  updateQty: (lineId, qty) =>
    set((state) => ({
      lines: state.lines.map((l) => (l.lineId === lineId ? { ...l, qty } : l)),
    })),

  updateDeliveryDate: (lineId, deliveryDate) =>
    set((state) => ({
      lines: state.lines.map((l) => (l.lineId === lineId ? { ...l, deliveryDate } : l)),
    })),

  toggleSelected: (lineId) =>
    set((state) => ({
      lines: state.lines.map((l) => (l.lineId === lineId ? { ...l, selected: !l.selected } : l)),
    })),

  clear: () => set({ lines: [] }),

  resetAll: () => set({ lines: [], qpo: INITIAL_QPO, heldQpos: [] }),

  // ── QPO slice ────────────────────────────────────────────────────────────
  qpo: INITIAL_QPO,
  setQpoSupplierInfo: (info) => set((s) => ({ qpo: { ...s.qpo, ...info, items: s.qpo.items } })),
  addInitialQpo: (qpoItem: QpoItem) =>
    set((s) => {
      if (s.qpo.items.some((i) => i.skuId === qpoItem.skuId)) return {}
      return { qpo: { ...s.qpo, items: [...s.qpo.items, qpoItem] } }
    }),
  loadQpoItems: (items: QpoItem[]) =>
    set((s) => ({ qpo: { ...s.qpo, items: items.map((i) => ({ ...i })) } })),
  updateQpoItem: (skuId, patch) =>
    set((s) => ({
      qpo: {
        ...s.qpo,
        items: s.qpo.items.map((i) => (i.skuId === skuId ? { ...i, ...patch } : i)),
      },
    })),
  deleteQpoItem: (skuId) =>
    set((s) => ({
      qpo: {
        ...s.qpo,
        items: s.qpo.items.filter((i) => i.skuId !== skuId),
      },
    })),
  resetQpoItems: () =>
    set((s) => ({
      qpo: {
        ...s.qpo,
        items: [],
      },
    })),
  resetQpo: () => {
    return set({ qpo: INITIAL_QPO })
  },

  heldQpos: [],
  holdQpo: () =>
    set((s) => ({
      heldQpos: [...s.heldQpos, { ...s.qpo, status: 'held' }],
      qpo: {
        ...INITIAL_QPO,
        items: MOCK_QPO_ITEMS.map((i) => ({ ...i })),
        classTempMap: initialClassTempMap(),
      },
    })),
  resumeQpo: (invoiceNo) =>
    set((s) => {
      const held = s.heldQpos.find((q) => q.invoiceNo === invoiceNo)
      if (!held) return {}
      return {
        qpo: { ...held, status: 'active' },
        heldQpos: s.heldQpos.filter((q) => q.invoiceNo !== invoiceNo),
      }
    }),
  submitQpo: () => set((s) => ({ qpo: { ...s.qpo, status: 'submitted' } })),

  updateClassTemp: (category, temp) =>
    set((s) => ({
      qpo: {
        ...s.qpo,
        classTempMap: {
          ...s.qpo.classTempMap,
          [category]: { temp, status: 'pending' },
        },
      },
    })),

  confirmClassTemp: (category) =>
    set((s) => ({
      qpo: {
        ...s.qpo,
        classTempMap: {
          ...s.qpo.classTempMap,
          [category]: { ...s.qpo.classTempMap[category], status: 'confirmed' },
        },
      },
    })),

  confirmClassTempAsFeedback: (category) =>
    set((s) => ({
      qpo: {
        ...s.qpo,
        classTempMap: {
          ...s.qpo.classTempMap,
          [category]: { ...s.qpo.classTempMap[category], status: 'confirmed-feedback' },
        },
      },
    })),

  rejectClassTemp: (category) =>
    set((s) => ({
      qpo: {
        ...s.qpo,
        classTempMap: {
          ...s.qpo.classTempMap,
          [category]: { ...s.qpo.classTempMap[category], status: 'rejected' },
        },
      },
    })),

  resetClassTemp: (category) =>
    set((s) => ({
      qpo: {
        ...s.qpo,
        classTempMap: {
          ...s.qpo.classTempMap,
          [category]: { ...s.qpo.classTempMap[category], status: 'pending' },
        },
      },
    })),
}))

/** Total number of cart line items (used by scanner cart bar + header badge). */
export function selectLineCount(state: { lines: CartLine[] }): number {
  return state.lines.length
}

// ── Selectors for the unguided lesson player ──────────────────────────────
// Pure functions over state, so the task evaluator can be unit-tested without
// React. Each one is typed structurally rather than against OrderingState so a
// test can hand it a literal.

/** Is this SKU in the cart at all, by any route? */
export function selectHasSku(state: { lines: CartLine[] }, skuId: string): boolean {
  return state.lines.some((l) => l.sku.id === skuId)
}

/**
 * How many DISTINCT products are in the cart.
 *
 * Distinct rather than `lines.length` because `addLine` creates a second line
 * for the same SKU on a different delivery date, so a line count would let the
 * same product satisfy a "three different products" task.
 */
export function selectDistinctSkuCount(state: { lines: CartLine[] }): number {
  return new Set(state.lines.map((l) => l.sku.id)).size
}

/** Some lines ticked but not all — what submitting a *partial* order requires. */
export function selectHasPartialSelection(state: { lines: CartLine[] }): boolean {
  const selected = state.lines.filter((l) => l.selected).length
  return state.lines.length > 1 && selected > 0 && selected < state.lines.length
}

/** Supplier identity captured, by QR scan or by hand. */
export function selectQpoSupplierSet(state: { qpo: QpoSession }): boolean {
  return state.qpo.supplier !== null
}

/** Every manually-entered supplier field filled in. */
export function selectQpoSupplierDetailsComplete(state: { qpo: QpoSession }): boolean {
  const { driverName, employeeCode, vehicleNo } = state.qpo
  return driverName.trim() !== '' && employeeCode.trim() !== '' && vehicleNo.trim() !== ''
}

/** Temperature for a class recorded AND dispositioned to the given status. */
export function selectClassTempStatus(
  state: { qpo: QpoSession },
  category: TempCheckCategory,
  status: 'confirmed' | 'confirmed-feedback',
): boolean {
  return state.qpo.classTempMap[category].status === status
}

export function selectQpoStatus(state: { qpo: QpoSession }, status: QpoSession['status']): boolean {
  return state.qpo.status === status
}

/** At least one QPO is parked on hold. */
export function selectHasHeldQpo(state: { heldQpos: QpoSession[] }): boolean {
  return state.heldQpos.length > 0
}

/**
 * Some line was received short of what was ordered.
 *
 * Strictly `<`, not `!==`: receiving MORE than ordered is an over-delivery,
 * a different exception with a different resolution, and must not tick a
 * shortfall task.
 */
export function selectHasShortfall(state: { qpo: QpoSession }): boolean {
  return state.qpo.items.some((i) => i.grQty < i.poQty)
}
